import React from 'react';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { processLink } from '../../shared/processLink';
import { AccentedText } from '../AccentedText/AccentedText';
import { ReachButton } from '../ReachButton/ReachButton';
import { TaggedImage, TaggedImageProps } from '../TaggedImage/TaggedImage';
import { HomepageHero } from './HomepageHero';
import { ParentNodeProvider } from '../../providers/ParentNodeProvider';
import './Hero.scss';
import HeroImage from './HeroImage';

export interface HeroProps {
    /** The number of columns */
    cols: 1 | 2;
    /** The main title of the hero */
    heading: string;
    /** The smaller text of the hero */
    subheading?: string;
    /** The hero image */
    heroImage?: IGatsbyImageData;
    /** The hero image alt text */
    heroImageAlt?: string;
    /** The hero video. Hero image takes precendence */
    heroVideo?: string;
    /** Name of of video used in Hero component - to be used for iframe title attr */
    videoName?: string;
    /** Whether this is a homepage hero. */
    homepage?: boolean;
    /** Link buttons to display in the hero. */
    callsToAction?: {
        title: string;
        uri: string;
    }[];
    /** Alignment of the hero image/video (only applicable for two column)  */
    align: `left` | `right`;
    /** Theme of the hero (background and text color) */
    theme: `white` | `gray` | `dark`;
    /** Eyebrow text of the hero. */
    eyebrow?: string;
    /** Children rendered in the hero. */
    children?: React.ReactElement;
    /** Tagged image to use instead of a hero image (heroImage will take precedence) */
    taggedImage?: TaggedImageProps;
    toggled?: boolean;
    ariaHidden?: boolean;
    heroImageActioinLink?: {
        title: string;
        uri: string;
    };
}

export const Hero = (props: HeroProps) => {
    if (props.homepage) {
        return (
            <ParentNodeProvider nodeName={props.heading} nodeType={`Hero`}>
                <HomepageHero
                    heading={props.heading}
                    heroImage={props.heroImage}
                    heroImageAlt={props.heroImageAlt}
                    subheading={props.subheading}
                    callsToAction={props.callsToAction}
                />
            </ParentNodeProvider>
        );
    }

    return (
        <ParentNodeProvider nodeName={props.heading} nodeType={`Hero`}>
            <header
                className={`themed-container hero ${props.theme} cols-${
                    props.cols
                } ${props.align ? props.align : ``} ${
                    props.toggled ? `collapsed` : ``
                }`}
                aria-hidden={props.ariaHidden}
            >
                <div className="kite-container">
                    <div className="col">
                        <h1>
                            {props.eyebrow && (
                                <span className="eyebrow">{props.eyebrow}</span>
                            )}
                            <AccentedText>{props.heading}</AccentedText>
                        </h1>

                        <p>{props.subheading}</p>
                        <section className="hero-children">
                            {props.children}
                        </section>

                        {props.callsToAction &&
                            props.callsToAction.length > 0 && (
                                <div className="buttons">
                                    {props.callsToAction
                                        .filter((link) => link)
                                        .map((link, idx) => (
                                            <ReachButton
                                                buttonStyle={
                                                    idx === 0
                                                        ? `primary`
                                                        : `secondary`
                                                }
                                                key={idx}
                                                href={processLink(link.uri)}
                                            >
                                                {link.title}
                                            </ReachButton>
                                        ))}
                                </div>
                            )}
                    </div>
                    {props.heroImage && (
                        <HeroImage
                            cols={props.cols}
                            image={props.heroImage}
                            alt={props.heroImageAlt ?? ``}
                            imageActionLink={
                                props?.heroImageActioinLink ?? undefined
                            }
                        />
                    )}

                    {props.taggedImage && !props.heroImage && (
                        <TaggedImage {...props.taggedImage} />
                    )}

                    {!props.heroImage &&
                        props.heroVideo &&
                        props.cols === 1 && (
                            <div className="video-wrapper">
                                <iframe
                                    src={`https://player.vimeo.com/video/${props.heroVideo}?=color=0099d8&title=0&byline=0&portrait=0`}
                                    width="100%"
                                    frameBorder="0"
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                    title={props.videoName}
                                ></iframe>
                            </div>
                        )}

                    {!props.heroImage &&
                        props.heroVideo &&
                        props.cols === 2 && (
                            <div className="video-wrapper component-error">
                                Videos can only be used in a one column hero.
                            </div>
                        )}
                </div>
            </header>
        </ParentNodeProvider>
    );
};
